import AddressAppResource from './AddressAppResource';
import AppResource from '@milestoneslabs/milestones-js-engine/src/Data/AppResource';
import CalendarAppResource from './CalendarAppResource';
import CalendarCategoryAppResource from './CalendarCategoryAppResource';
import CalendarEventAppResource from './CalendarEventAppResource';
import CalendarEventCategoryAppResource from './CalendarEventCategoryAppResource';
import ContactAppResource from './ContactAppResource';
import EmailAppResource from './EmailAppResource';
import FileAppResource from './FileAppResource';
import OrganizationMemberAppResource from './OrganizationMemberAppResource';
import PhoneAppResource from './PhoneAppResource';
import PropertyInstanceAppResource from './PropertyInstanceAppResource';
import ServiceTicketAppResource from './ServiceTicketAppResource';
import StageRoleAppResource from './StageRoleAppResource';
import StageTypeAppResource from './StageTypeAppResource';
import TaskAppResource from './TaskAppResource';
import TaskCategoryAppResource from './TaskCategoryAppResource';
import TaskPriorityAppResource from './TaskPriorityAppResource';
import TaskRoleAppResource from './TaskRoleAppResource';
import TaskStatusAppResource from './TaskStatusAppResource';
import UserAppResource from './UserAppResource';
import UserInvitationAppResource from './UserInvitationAppResource';
import WorkspaceAppResource from './WorkspaceAppResource';
import WorkspaceTypeAppResource from './WorkspaceTypeAppResource';
import BusinessProfileAppResource from './BusinessProfileAppResource';
import FormsetAppResource from "./FormsetAppResource";
import OrganizationMoxiCompanyAppResource from "./OrganizationMoxiCompanyAppResource";
import OrganizationMoxiOfficeAppResource from "./OrganizationMoxiOfficeAppResource";
import Engine from "@milestoneslabs/milestones-js-engine";
import MLSAppResource from "./MLSAppResource";
import OrganizationBrandingAppResource from "./OrganizationBrandingAppResource";
import ServiceProviderTypeAppResource from "./ServiceProviderTypeAppResource";
import OrganizationSendgridSenderAppResource
    from "./OrganizationSendgridSenderAppResource";
import HubImportTokenAppResource from './HubImportTokenAppResource';
import OrganizationDisclaimerAppResource from './OrganizationDisclaimerAppResource';
import OrganizationSubscriptionPlanAppResource
    from './OrganizationSubscriptionPlanAppResource';
import OrganizationSettingAppResource from './OrganizationSettingAppResource';

export const STATUS = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
};

export const TYPE = {
    ASSOCIATION: 'association',
    COMPANY: 'company',
    DIVISION: 'division',
    OFFICE: 'office',
    BRANCH: 'branch',
    TEAM: 'team',
    GROUP: 'group',
    SELF: 'self',
    OTHER: 'other',
};

class OrganizationAppResource extends AppResource {

    /**
     * @returns {string}
     */
    appResourceType() {
        return 'Organization';
    }

    /**
     * @returns {string}
     */
    appResourceApiPrefix() {
        return 'organizations';
    }

    /**
     * @returns {*[]}
     */
    hidden() {
        return super.hidden().concat([
            'uuid',
            'deleted_at',
        ]);
    }

    /**
     * @returns {*[]}
     */
    fillable() {
        return [
            'type',
            'name',
            'description',
            'ownerUserUuid',
            'parentOrganizationUuid',
            'status',
            'ownerUser',
            'parentOrganization',
            'files',
            'propertyInstances',
            'workspaces',
            'workspaceTypes',
            'stageTypes',
            'stageRoles',
            'childrenOrganizations',
            'members',
            'users',
            'contacts',
            'tasks',
            'taskCategories',
            'taskPriorities',
            'taskStatuses',
            'taskRoles',
            'calendarCategories',
            'calendars',
            'calendarEventCategories',
            'calendarEvents',
            'emails',
            'phones',
            'addresses',
            'userInvitations',
            'addressesMeta',
            'emailsMeta',
            'phonesMeta',
            'ownerUser',
            'isSystem',
            'hpHsCreateStatus',
            'branding',
            'resources',
            'settings',
            'enabledFormsets',
            'moxiCompanies',
            'moxiOffices',
            'brandingPath',
            'mlses',
            'excludedServiceProviderTypes',
            'disableDefaultPlaybooks',
            'disableActivityHistory',
            'plan',
            'administrators',
            'sendgridApiKey',
            'sendgridStatus',
            'platformBrandingEnabled',
            'dailyInvitationLimit',
            'privacyPolicyUrl',
            'canRemoveHubOwners',
            'spLocationFilter',
            'organizationSettings',
            'restrictServiceProviders',
            'corelogicId',
        ];
    }

    /**
     * @returns {*[]}
     */
    relationMap() {
        return {
            ownerUser: UserAppResource,
            parentOrganization: OrganizationAppResource,
            files: FileAppResource,
            emails: EmailAppResource,
            phones: PhoneAppResource,
            addresses: AddressAppResource,
            propertyInstances: PropertyInstanceAppResource,
            workspaces: WorkspaceAppResource,
            workspaceTypes: WorkspaceTypeAppResource,
            stageTypes: StageTypeAppResource,
            stageRoles: StageRoleAppResource,
            childrenOrganizations: OrganizationAppResource,
            members: OrganizationMemberAppResource,
            users: UserAppResource,
            contacts: ContactAppResource,
            tasks: TaskAppResource,
            taskCategories: TaskCategoryAppResource,
            taskPriorities: TaskPriorityAppResource,
            taskStatuses: TaskStatusAppResource,
            taskRoles: TaskRoleAppResource,
            calendarCategories: CalendarCategoryAppResource,
            calendars: CalendarAppResource,
            calendarEventCategories: CalendarEventCategoryAppResource,
            calendarEvents: CalendarEventAppResource,
            serviceTickets: ServiceTicketAppResource,
            userInvitations: UserInvitationAppResource,
            businessProfile: BusinessProfileAppResource,
            enabledFormsets: FormsetAppResource,
            moxiCompanies: OrganizationMoxiCompanyAppResource,
            moxiOffices: OrganizationMoxiOfficeAppResource,
            mlses: MLSAppResource,
            organizationBranding: OrganizationBrandingAppResource,
            excludedServiceProviderTypes: ServiceProviderTypeAppResource,
            administrators: OrganizationMemberAppResource,
            sendGridSenders: OrganizationSendgridSenderAppResource,
            hubImportTokens: HubImportTokenAppResource,
            disclaimer: OrganizationDisclaimerAppResource,
            activeDisclaimer: OrganizationDisclaimerAppResource,
            organizationSubscriptionPlans: OrganizationSubscriptionPlanAppResource,
            organizationSettings: OrganizationSettingAppResource,
        };
    }

    /**
     * @returns {*[]}
     */
    relationForeignKeys() {
        return {
            ownerUser: 'owner_user_uuid',
            parentOrganization: 'parent_organization_uuid',
        };
    }

    /**
     * @returns {*}
     */
    validationRules() {

        let rules = {
            ownerUserUuid: {
                type: 'string',
                presence: true,
            },
            ownerUser: {
                type: 'string',
            },
            parentOrganizationUuid: {
                type: 'string',
            },
            type: {
                type: 'string',
                presence: true,
                length: {
                    minimum: 1,
                    maximum: 255,
                },
                inclusion: [
                    'association',
                    'company',
                    'division',
                    'office',
                    'branch',
                    'team',
                    'group',
                    'self',
                    'other',
                ],
            },
            name: {
                type: 'string',
                presence: true,
                length: {
                    minimum: 1,
                    maximum: 255,
                },
            },
            description: {
                type: 'string',
            },
            status: {
                type: 'string',
                length: {
                    minimum: 1,
                    maximum: 255,
                },
            },
            addresses: {
                array: {
                    items: {
                        type: 'string',
                    },
                },
            },
            addressesMeta: {
                array: {
                    items: {
                        array: {
                            items: {
                                type: 'string',
                            },
                        },
                    },
                },
            },
            emails: {
                array: {
                    items: {
                        type: 'string',
                    },
                },
            },
            emailsMeta: {
                array: {
                    items: '',
                },
            },
            phones: {
                array: {
                    items: {
                        type: 'string',
                    },
                },
            },
            phonesMeta: {
                array: {
                    items: '',
                },
            },
            isSystem: {
                type: 'boolean',
            },
            hpHsCreateStatus: {
                type: 'boolean',
            },
            settings: {},
            moxiCompanies: {
                type: 'array'
            },
            moxiOffices: {
                type: 'array'
            },
            brandingPath: {
                type: 'string',
            },
            disableDefaultPlaybooks: {
                type: 'boolean',
            },
            disableActivityHistory: {
                type: 'boolean',
            },
            plan: {
                type: 'string',
            },
            sendgridApiKey: {
                type: 'string',
            },
            sendgridStatus: {
                type: 'boolean',
            },
            platformBrandingEnabled: {
                type: 'boolean',
            },
            dailyInvitationLimit: {
                type: 'integer',
            },
            privacyPolicyUrl: {
                type: 'string',
            },
            canRemoveHubOwners: {
                type: 'boolean',
            },
            spLocationFilter: {
                type: 'integer',
            },
            restrictServiceProviders: {
                type: 'boolean',
            },
            corelogicId: {
                type: 'string',
            },
        };

        if (this.uuid) {
            rules = {
                ...rules,
                /*branding values are generated in the backend at creation.*/
                'branding.signIn.customer.link.value': {
                    format: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                },
                'branding.signIn.customer.headline.value': {
                    presence: false,
                    length: {
                        minimum: 3,
                        maximum: 255,
                    },
                },
                'branding.signIn.customer.instructions.value': {
                    presence: false,
                    length: {
                        minimum: 3,
                        maximum: 255,
                    },
                },
                'branding.signIn.customer.callToAction.value': {
                    presence: false,
                    length: {
                        minimum: 3,
                        maximum: 255,
                    },
                },
                'branding.signIn.customer.loginDisclaimer.value': {
                    presence: false,
                    length: {
                        minimum: 0,
                        maximum: 1400,
                    },
                },
                'branding.signIn.customer.benefits.values': {
                    arrayOfObjects: {
                        value: {
                            length: {
                                maximum: 255,
                            },
                        },
                        status: {
                            presence: false,
                        },
                    },
                },
                'branding.signIn.customer.links.values': {
                    arrayOfObjects: {
                        value: {
                            emptyUrl: {
                                message: 'Invalid Url',
                                fullMessages: false,
                            },
                        },
                        label: {
                            length: {
                                maximum: 255,
                                message: 'Label is too long (maximum is 255 characters)',
                            },
                        },
                    },
                },
                'branding.signIn.customer.footer.status': {
                    presence: false,
                },

                'branding.signIn.member.headline.value': {
                    presence: false,
                    length: {
                        minimum: 3,
                        maximum: 255,
                    },
                },
                'branding.signIn.member.instructions.value': {
                    presence: false,
                    length: {
                        maximum: 255,
                    },
                },
                'branding.signIn.member.callToAction.value': {
                    presence: false,
                    length: {
                        minimum: 3,
                        maximum: 255,
                    },
                },
                'branding.signIn.member.loginDisclaimer.value': {
                    presence: false,
                    length: {
                        minimum: 0,
                        maximum: 1400,
                    },
                },
                'branding.signIn.member.benefits.values': {
                    arrayOfObjects: {
                        value: {
                            length: {
                                minimum: 3,
                                maximum: 255,
                            },
                        },
                        status: {
                            presence: false,
                        },
                    },
                },
                'branding.signIn.member.links.values': {
                    arrayOfObjects: {
                        value: {
                            emptyUrl: true,
                        },
                        label: {
                            length: {
                                maximum: 255,
                            },
                        },
                    },
                },
                'branding.signIn.member.footer.status': {
                    presence: false,
                },
            };
        }

        return rules;
    }


    get moxiNumericIds() {
        let numericIds = [];
        if (this.moxiCompanies && this.moxiCompanies.isNotEmpty) {
            this.moxiCompanies.each(item => {
                if (item.numericId) {
                    numericIds.push(item.numericId);
                }
            });
        }
        return numericIds;
    }

    get moxiOfficeIds() {
        let ids = [];
        if (this.moxiOffices && this.moxiOffices.isNotEmpty) {
            this.moxiOffices.each(item => {
                ids.push(item.moxiWorksOfficeId);
            });
        }

        return ids;
    }

    get ownerOrganizationMember() {
        if (!this.members) {
            return;
        }
        return this.members.toArray().find(member => member.userUuid === this.ownerUserUuid);
    }

    apiPaths (environment = null) {
        return {
            'transactionsImport': Engine.apiUrl(this.appResourceApiPrefix() + '/{uuid}/transactions/import', environment),
            'contactsImport': Engine.apiUrl(this.appResourceApiPrefix() + '/{uuid}/contacts/import', environment),
            'listingsImport': Engine.apiUrl(this.appResourceApiPrefix() + '/{uuid}/listings/import', environment),
            'getOrganizationSources': Engine.apiUrl(this.appResourceApiPrefix() + '/{uuid}/contact/sources', environment),
            'getOrganizationGroups': Engine.apiUrl(this.appResourceApiPrefix() + '/{uuid}/contact/groups', environment),
            'getOrganizationOfficeStatistics': Engine.apiUrl(this.appResourceApiPrefix() + '/{uuid}/office/statistics', environment),
            'platformBranding': Engine.apiUrl(this.appResourceApiPrefix() + '/{uuid}/platform/branding', environment),
            'organizationRelative': Engine.apiUrl(this.appResourceApiPrefix() + '/{uuid}/relative/{parentUuid}', environment),
            ...super.apiPaths(),
        }
    }

    async importMoxiTransactions(data = [], environment = null) {
        if (this.uuid === null) {
            return Promise.resolve(null);
        }

        if (!data || !data.agents) {
            return Promise.resolve(null);
        }

        const url = this.apiPaths(environment).transactionsImport.replace('{uuid}', this.uuid);

        const apiResponse = await Engine.post(url, data);
        return apiResponse;
    }

    async importMoxiContacts(data = [], environment = null) {
        if (this.uuid === null) {
            return Promise.resolve(null);
        }

        if (!data || !data.agents) {
            return Promise.resolve(null);
        }

        const url = this.apiPaths(environment).contactsImport.replace('{uuid}', this.uuid);

        return await Engine.post(url, data);
    }

    async importMoxiListings(data = [], environment = null) {
        if (this.uuid === null || !data || !data.agents) {
            return Promise.resolve(null);
        }

        const url = this.apiPaths(environment).listingsImport.replace('{uuid}', this.uuid);

        return await Engine.post(url, data);
    }

    async getMoxiOrganizationSources(environment = null) {
        if (this.uuid === null) {
            return Promise.resolve(null);
        }

        const url = this.apiPaths(environment).getOrganizationSources.replace('{uuid}', this.uuid);
        return await Engine.get(url);
    }

    async getMoxiOrganizationGroups(data = [], environment = null) {
        if (this.uuid === null || !data || !data.agents) {
            return Promise.resolve(null);
        }

        const url = this.apiPaths(environment).getOrganizationGroups.replace('{uuid}', this.uuid);
        return await Engine.post(url, data);
    }

    async getOrganizationMoxiImportStatistics(environment) {
        if (this.uuid === null) {
            return Promise.resolve(null);
        }

        const url = this.apiPaths(environment).getOrganizationOfficeStatistics.replace('{uuid}', this.uuid);
        return await Engine.get(url);
    }

    async getPlatformBrandingData(organizationUuid, environment = null) {
        const url = this.apiPaths(environment).platformBranding.replace('{uuid}', organizationUuid);

        return await Engine.get(url);
    }

    /**
     * Checks if the current organization is a child of the given parent organization.
     *
     * This will also return the organization being searched for
     * (e.g., window.env.OHO_REAL_ESTATE_ORGANIZATION_UUID) in the response payload,
     * allowing you to validate or extract the relevant relationship context.
     */
    async isChildOf(parentUuid, environment = null) {
        if (!this.uuid || !parentUuid) {
            return Promise.resolve(null);
        }

        const url = this.apiPaths(environment).organizationRelative.replace('{uuid}', this.uuid).replace('{parentUuid}', parentUuid);
        return await Engine.get(url);
    }
}

export default OrganizationAppResource;
